import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AlertController, Events } from "ionic-angular";
import { CanvasService } from "../../../canvas/canvas-service.interface";
import {
  canvas,
  NewWoundData,
  stepOneSidebar,
} from "../new-wound-site-service";
import { NewWoundSiteTabsComponent } from "../new-wound-site-tabs/new-wound-site-tabs";
import { NewWoundService } from "../new-wound.service";

@Component({
  selector: "new-wound-sidebar",
  templateUrl: "new-wound-sidebar.html",
})
export class NewWoundSidebarComponent implements AfterViewInit {
  @Input() canvasService: CanvasService;
  @Input() config;
  @Input() type;
  @Input() readOnly: boolean;
  @Output() onDataUpdate: EventEmitter<any> = new EventEmitter();
  @Output() onWoundToolMigrationWarningUpdate = new EventEmitter<boolean>();
  private sidebar: stepOneSidebar;
  data: NewWoundData;
  points = [];
  subscriptions: Array<any> = [];
  locationTitle: any;
  woundType: any;
  showWoundType: boolean = false;
  stage: any;
  exudateLevel: any;
  showExudateLevel: boolean = false;
  consistency: any;
  showConsistency: boolean = false;
  drainageType: any;
  showDrainage: boolean = false;
  locationType: any;
  unableToMeasure: any;
  summaryHeader: string;
  tools: any;

  woundTypeMap: { [key: string]: string } = {
    arterialUlcers: "ulcersOtherThanDiabeticOrPressureWound",
    buruliUlcer: "ulcersOtherThanDiabeticOrPressureWound",
    decubitusUlcer: "pressureUlcerInjuryWound",
    neuropathicUlcer: "ulcersOtherThanDiabeticOrPressureWound",
    pressureUlcer: "pressureUlcerInjuryWound",
    skinTear: "skinTearWound",
    venousStasisUlcers: "ulcersOtherThanDiabeticOrPressureWound",
  };
  unmappedWoundType = "lymphedema";

  constructor(
    private newWoundService: NewWoundService,
    private cdr: ChangeDetectorRef,
    public events: Events,
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private newWoundSiteTabs: NewWoundSiteTabsComponent
  ) {}

  ngOnInit(): void {
    this.onWoundToolMigrationWarningUpdate.emit(false);

    this.locationTitle = this.config.locationTitle
      ? this.config.locationTitle
      : null;
    this.woundType = this.config.woundType ? this.config.woundType : null;
    this.stage = this.config.stage ? this.config.stage : null;
    this.exudateLevel = this.config.exudateLevel
      ? this.config.exudateLevel
      : null;
    this.consistency = this.config.consistency ? this.config.consistency : null;
    this.drainageType = this.config.drainageType
      ? this.config.drainageType
      : null;
    this.locationType = this.config.locationType
      ? this.config.locationType
      : null;
    this.tools = this.config.tools ? this.config.tools : null;

    this.sidebar = this.clearSidebarData();
    this.newWoundService.getCurrentPointData().subscribe((currentWound) => {
      if (currentWound) {
        this.data = currentWound;
        let locationIndex = currentWound.locationIndex + 1;
        if (currentWound.stepOneSidebarData) {
          if (
            this.woundTypeMap[currentWound.stepOneSidebarData.woundType] ||
            currentWound.stepOneSidebarData.woundType === this.unmappedWoundType
          ) {
            currentWound.stepOneSidebarData.originalWoundType =
              currentWound.stepOneSidebarData.woundType;
          }
          if (
            this.woundTypeMap[currentWound.stepOneSidebarData.woundType] ||
            currentWound.stepOneSidebarData.migrated ||
            currentWound.stepOneSidebarData.woundType === this.unmappedWoundType
          ) {
            currentWound.stepOneSidebarData.migrated = true;
            this.onWoundToolMigrationWarningUpdate.emit(true);
          } else {
            this.onWoundToolMigrationWarningUpdate.emit(false);
          }

          this.sidebar = currentWound.stepOneSidebarData;
          this.sidebar.woundType =
            this.woundTypeMap[this.sidebar.woundType] || this.sidebar.woundType;
          if (this.sidebar.woundType === this.unmappedWoundType) {
            this.sidebar.woundType = "";
          }
          if (currentWound.stepOneSidebarData.locationTitle) {
            this.summaryHeader =
              locationIndex +
              ". " +
              currentWound.stepOneSidebarData.locationTitle;
          }
        } else {
          this.sidebar = this.clearSidebarData();
        }

        this.data.stepOneSidebarData = this.sidebar;
      }
    });
  }

  clearSidebarData() {
    return {
      locationTitle: "",
      locationType: "body",
      woundType: "",
      woundTypeOther: "",
      width: "",
      length: "",
      depth: "",
      tracking: false,
      unableToMeasure: false,
      trackingOther: "",
      undermining: false,
      underminingOther: "",
      stage: "",
      exudateLevel: "",
      exudateLevelOther: "",
      consistency: "",
      consistencyOther: "",
      drainageType: "",
      drainageTypeOther: "",
      toolName: "",
      severity: null,
      migrated: false,
    };
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this.canvasService.getAllPointDataObservable().subscribe((data) => {
        if (!data || !data.points) {
          return;
        }
        this.points = data.points;
      })
    );
    this.cdr.detectChanges();
  }

  selectPoint(index: number): void {
    this.canvasService.selectPoint(index);
  }

  addPoint(): void {
    this.canvasService.addPoint();
  }

  categorySelected(locationCategory) {
    this.canvasService.setCategory(locationCategory);
    this.sidebar.locationType = locationCategory;
    this.onDataUpdate.emit(this.data);
  }

  exudateLevelSelected(exudateLevel) {
    this.sidebar.exudateLevel = exudateLevel;
    this.onDataUpdate.emit(this.data);
  }
  ngOnDestroy() {
    for (let sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.subscriptions = [];
  }

  setData(data, field) {
    if (field === "locationTitle") {
      this.sidebar.locationTitle = data;
    } else if (field === "woundType") {
      this.sidebar.woundType = data;
      if (data === "other") {
        this.showWoundType = true;
      } else {
        this.showWoundType = false;
        this.sidebar.woundTypeOther = "";
      }
    } else if (field === "woundTypeOther") {
      this.sidebar.woundTypeOther = data;
    } else if (field === "length") {
      var val = this.validateNumber(data.target.value);
      if (val == false) {
        data.target.value = "";
      }
      this.sidebar.length = data.target.value;
    } else if (field === "width") {
      var val = this.validateNumber(data.target.value);
      if (val == false) {
        data.target.value = "";
      }
      this.sidebar.width = data.target.value;
    } else if (field === "depth") {
      var val = this.validateNumber(data.target.value);
      if (val == false) {
        data.target.value = "";
      }
      this.sidebar.depth = data.target.value;
    } else if (field === "unableToMeasure") {
      this.sidebar.unableToMeasure = data.value;
    } else if (field === "tracking") {
      this.sidebar.tracking = data.value;
      if (data.value) {
      } else {
        this.sidebar.trackingOther = "";
      }
    } else if (field === "trackingOther") {
      this.sidebar.trackingOther = data.value;
    } else if (field === "undermining") {
      this.sidebar.undermining = data.value;
      if (data.value) {
      } else {
        this.sidebar.underminingOther = "";
      }
    } else if (field === "underminingOther") {
      this.sidebar.underminingOther = data.value;
    } else if (field === "stage") {
      this.sidebar.stage = data;
    } else if (field === "exudateLevel") {
      this.sidebar.exudateLevel = data;
      if (data === "other") {
        this.showExudateLevel = true;
      } else {
        this.showExudateLevel = false;
        this.sidebar.exudateLevelOther = "";
      }
    } else if (field === "exudateLevelOther") {
      this.sidebar.exudateLevelOther = data.value;
    } else if (field === "consistency") {
      this.sidebar.consistency = data;
      if (data === "other") {
        this.showConsistency = true;
      } else {
        this.showConsistency = false;
        this.sidebar.consistencyOther = "";
      }
    } else if (field === "consistencyOther") {
      this.sidebar.consistencyOther = data.value;
    } else if (field === "drainageType") {
      if (data.indexOf("none") >= 0) {
        if (data.length > 1) {
          data.splice(0, data.length);
          data = ["none"];
        }
      }
      this.sidebar.drainageType = data;
      if (data && data.indexOf("other") >= 0) {
        this.showDrainage = true;
      } else {
        this.showDrainage = false;
        this.sidebar.drainageTypeOther = "";
      }
    } else if (field === "drainageTypeOther") {
      this.sidebar.drainageTypeOther = data.value;
    } else if (field === "toolName") {
      this.sidebar.toolName = data;
    }

    if (this.data) {
      this.data.stepOneSidebarData = this.sidebar;
      this.onDataUpdate.emit(this.data);
    }
  }

  getLimitedCharacters(data, field, maxlength) {
    if (data.target.value) {
      if (field === "length") {
        this.sidebar.length = data.target.value.substring(0, maxlength + 4);
        // var num = this.formatNumber(this.sidebar.length, maxlength);
        // this.sidebar.length = num.toString();
        // if(this.sidebar.length.length > maxlength + 4)
        // {
        //   this.sidebar.length = this.sidebar.length.slice(0, maxlength + 4);
        //   var num = this.formatNumber(this.sidebar.length, maxlength);
        // }
      } else if (field === "width") {
        this.sidebar.width = data.target.value.substring(0, maxlength + 4);
        // var num = this.formatNumber(this.sidebar.width, maxlength);
        // this.sidebar.width = num.toString();
        // if(this.sidebar.width.length > maxlength + 4)
        // {
        //   this.sidebar.width = this.sidebar.width.slice(0, maxlength + 4);
        //   var num = this.formatNumber(this.sidebar.width, maxlength);
        // }
        // this.sidebar.width = num.toString();
      } else if (field === "depth") {
        this.sidebar.depth = data.target.value.substring(0, maxlength + 4);
        // var num = this.formatNumber(this.sidebar.depth, maxlength);
        // this.sidebar.depth = num.toString();
        // if(this.sidebar.depth.length > maxlength + 4)
        // {
        //   this.sidebar.depth = this.sidebar.depth.slice(0, maxlength + 4);
        //   var num = this.formatNumber(this.sidebar.depth, maxlength);
        // }
        // this.sidebar.depth = num.toString();
      }
    }
  }

  formatNumber(x, maxlength) {
    // convert it to a string
    //   var s = "" + x;
    //   // if x is integer, the point is missing, so add it
    //   if (s.indexOf(".") == 0) {
    //     s = "0" + s;
    //   }
    //   if (s.indexOf(".") == -1) {
    //     s += "." + "00";
    //   }
    //  // make sure if we have at least 2 decimals
    //   //s += "00";
    //   // get the first 2 decimals
    //   if(s.indexOf(".") > 3)
    //   {
    //      return s = s.substring(0, 3) + ".000";
    //   }
    //   else
    //   {
    //     return s.substring(0, s.indexOf(".") + 4);
    //   }
  }

  validateNumber(data) {
    return true;
  }

  clearData() {
    let alert = this.alertCtrl.create({
      title: this.translate.instant("confirmWoundDelete"),
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Proceed",
          handler: () => {
            if (this.data.woundStatus === "reassess") {
              this.clearReassessData();
            } else {
              this.emptyData();
            }
          },
        },
      ],
    });
    alert.present();
  }

  emptyData() {
    this.sidebar = {
      locationTitle: "",
      locationType: "body",
      woundType: "",
      woundTypeOther: "",
      width: "",
      length: "",
      depth: "",
      tracking: false,
      trackingOther: "",
      undermining: false,
      underminingOther: "",
      stage: "",
      exudateLevel: "",
      exudateLevelOther: "",
      consistency: "",
      consistencyOther: "",
      drainageType: "",
      drainageTypeOther: "",
      unableToMeasure: false,
      toolName: "",
      severity: null,
      migrated: false,
    };
    this.canvasService.removePoint(this.data.locationIndex);
    this.data.canvasData = {
      status: "imputed",
    };
    this.data.notes.additionalNotes = "";
    this.data.notes.woundCareTreatment = "";
    this.data.score.tool = "";
    this.data.score.value = null;
    this.data.stepOneSidebarData = this.sidebar;
    this.canvasService.addPointData({
      x: undefined,
      y: undefined,
      category: "body",
      locationIndex: this.data.locationIndex,
      active: true,
      woundStatus: "inprogress",
    });
    this.canvasService.selectPoint(this.data.locationIndex);
    this.newWoundService.setCurrentPointData(this.data);
    this.newWoundSiteTabs.goToWoundLocation();
  }

  clearReassessData() {
    this.data.stepOneSidebarData.width = "";
    this.data.stepOneSidebarData.length = "";
    this.data.stepOneSidebarData.depth = "";
    this.data.stepOneSidebarData.tracking = false;
    this.data.stepOneSidebarData.trackingOther = "";
    this.data.stepOneSidebarData.undermining = false;
    this.data.stepOneSidebarData.underminingOther = "";
    this.data.stepOneSidebarData.stage = "";
    this.data.stepOneSidebarData.exudateLevel = "";
    this.data.stepOneSidebarData.exudateLevelOther = "";
    this.data.stepOneSidebarData.consistency = "";
    this.data.stepOneSidebarData.consistencyOther = "";
    this.data.stepOneSidebarData.drainageType = "";
    this.data.stepOneSidebarData.drainageTypeOther = "";
    this.data.stepOneSidebarData.unableToMeasure = false;
    this.data.stepOneSidebarData.toolName = "";
    this.data.stepOneSidebarData.severity = null;
    this.data.notes.additionalNotes = "";
    this.data.notes.woundCareTreatment = "";
    this.data.score.tool = "";
    this.data.score.value = null;
    let prevWound = this.newWoundService.getPreviousWoundStatus();
    if (prevWound) {
      let woundClicked = this.newWoundService.getPointDataByIndex(
        prevWound.index
      );
      woundClicked.woundStatus = prevWound.status;
      this.newWoundService.updateOrPushCurrentPoint(woundClicked);
      this.data.woundStatus = prevWound.status;
    }
    this.addDefaultPoint(this.canvasService["dataSet"].length);
    this.newWoundSiteTabs.goToWoundLocation();
  }

  addDefaultPoint(index: number) {
    let newCanvas: canvas = {
      status: "imputed",
    };

    let woundData: NewWoundData = {
      stepOneSidebarData: {
        locationTitle: "",
        locationType: "body",
        woundType: "",
        woundTypeOther: "",
        width: "",
        length: "",
        depth: "",
        unableToMeasure: false,
        tracking: false,
        trackingOther: "",
        undermining: false,
        underminingOther: "",
        stage: "",
        exudateLevel: "",
        exudateLevelOther: "",
        consistency: "",
        consistencyOther: "",
        drainageType: "",
        drainageTypeOther: "",
        toolName: "",
        severity: null,
        migrated: false,
      },
      canvasData: newCanvas,
      locationIndex: index,
      recordStatus: "open",
      woundStatus: "inprogress",
      isReassessed: false,
      reAccessedVisitId: "",
      notes: { woundCareTreatment: "", additionalNotes: "" },
      score: { tool: "", value: null },
    };
    this.canvasService.addPointData({
      x: undefined,
      y: undefined,
      category: "body",
      locationIndex: index,
      active: true,
      woundStatus: "inprogress",
    });
    this.canvasService.selectPoint(index);
    this.newWoundService.pushTheCurrentDataToArr(woundData);
    this.newWoundService.setCurrentPointData(woundData);
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/new-wound-site-card/new-wound-sidebar/new-wound-sidebar.ts